import * as Sentry from '@sentry/react';
// Using fetch API instead of AWS SDK to avoid additional dependencies

export interface CognitoUser {
  id: string;
  username: string;
  email: string;
  isAuthenticated: boolean;
  accessToken?: string;
  idToken?: string;
  refreshToken?: string;
}

export interface CognitoChallenge {
  challengeName: string;
  session: string;
  userAttributes?: any;
}

export interface CognitoConfig {
  userPoolId: string;
  clientId: string;
  region: string;
  clientSecret?: string;
}

class CognitoAuthService {
  private config: CognitoConfig;

  constructor(config: CognitoConfig) {
    this.config = config;
  }


  async signIn(email: string, password: string): Promise<{ user: CognitoUser | null; challenge: CognitoChallenge | null }> {
    try {
      console.log('Cognito signIn attempt:', { email, region: this.config.region, clientId: this.config.clientId });
      
      // Use backend API for authentication (more reliable than direct Cognito API)
      // Import config to get the correct backend host
      const { config } = await import('../config/environment');
      const backendUrl = config.environment === 'fake' || config.environment === 'local'
        ? `http://${config.restaurantBackendHost}/api/cognito-login`
        : `https://${config.restaurantBackendHost}/api/cognito-login`;
      
      console.log('Backend URL:', backendUrl);
      console.log('Environment config:', { environment: config.environment, backendHost: config.restaurantBackendHost });
      
      const backendResponse = await fetch(backendUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
        }),
      });

      if (!backendResponse.ok) {
        const errorText = await backendResponse.text();
        console.error('Backend authentication error:', errorText);
        throw new Error(`Backend authentication failed: ${backendResponse.status} ${errorText}`);
      }

      const backendData = await backendResponse.json();
      
      // Check for NEW_PASSWORD_REQUIRED challenge
      if (backendData.challenge && backendData.challenge.challengeName === 'NEW_PASSWORD_REQUIRED') {
        console.log('NEW_PASSWORD_REQUIRED challenge detected');
        return {
          user: null,
          challenge: {
            challengeName: backendData.challenge.challengeName,
            session: backendData.challenge.session,
            userAttributes: backendData.challenge.userAttributes
          }
        };
      }
      
      if (backendData.success && backendData.tokens) {
        const { accessToken, idToken, refreshToken } = backendData.tokens;
        const userInfo = this.parseIdToken(idToken);
        
        const user: CognitoUser = {
          id: userInfo.sub,
          username: userInfo.email,
          email: userInfo.email,
          isAuthenticated: true,
          accessToken,
          idToken,
          refreshToken,
        };

        this.storeTokens(accessToken, idToken, refreshToken);
        return { user, challenge: null };
      }

      // Handle authentication challenges or errors
      if (backendData.error) {
        throw new Error(backendData.error);
      }

      return { user: null, challenge: null };
    } catch (error) {
      console.error('Cognito sign in error:', error);
      Sentry.captureException(error);
      throw error;
    }
  }

  /**
   * Exchange the stored Cognito refresh token for a fresh access (+ id) token
   * via the public InitiateAuth REFRESH_TOKEN_AUTH flow — the same direct-Cognito
   * pattern signOut uses for GlobalSignOut. feat-028: the SPA sends the Cognito
   * access token (feat-026) but never refreshed it, so a >1h session sent an
   * expired token → 401 under feat-023/027 enforce. This keeps it fresh.
   *
   * On success, stores the new access/id tokens (the refresh token is reused —
   * REFRESH_TOKEN_AUTH does not return a new one) and returns them. Returns null
   * when there is no refresh token or Cognito rejects it (expired/revoked), so
   * the caller can route to login. Never logs token bytes.
   */
  async refreshSession(): Promise<{ accessToken: string; idToken: string } | null> {
    try {
      const refreshToken = localStorage.getItem('cognito_refresh_token');
      if (!refreshToken) {
        return null;
      }

      const authParameters: Record<string, string> = {
        REFRESH_TOKEN: refreshToken,
      };

      // SECRET_HASH keys on whatever identifier the request itself carries.
      // REFRESH_TOKEN_AUTH carries no `Username`, so Cognito can only validate
      // against the token's *canonical* username — the `cognito:username` claim
      // (the `sub` UUID) — NOT the email. Keying it on email yields 400 "Unable
      // to verify secret hash" (feat-030). Login works because it passes an
      // explicit `Username` and Cognito resolves the email alias during the
      // call; refresh has no alias to resolve, so it must use the canonical
      // username here.
      //
      // NOTE (feat-163, 2026-07-16): this block previously claimed "the
      // staging/dev client has none" — that is FALSE and was load-bearing.
      // Live read of the dev pool that day (us-west-1_yjiLHZSg4, client
      // 4g0mq10biks1ssh202cobos1vo, `bespoke-dev`): ClientSecret is PRESENT.
      // The claim propagated from here into feat-163's spec, its ship plan and
      // the MANIFEST, where it became "staging cannot rehearse SECRET_HASH, so
      // that path first executes in prod." It can. Verify against the live pool
      // before trusting any statement here — a standing claim about mutable
      // click-ops config silently going stale is the defect this note exists to
      // prevent, so date any claim you add.
      //
      // Consequence, pre-existing and NOT feat-163's to fix (filed with the
      // exposed-client-secret security item): the dev/staging entry in
      // environment.ts carries no clientSecret, so the guard below is false on
      // staging and no SECRET_HASH is sent to a client that requires one →
      // Cognito 400 → the null return below → the session silently drops to
      // login after ~1h. The fix is a server-side refresh proxy, NOT adding the
      // secret to this bundle: a secret in an SPA bundle is not a secret.
      if (this.config.clientSecret) {
        const idToken = localStorage.getItem('cognito_id_token');
        const claims = idToken ? this.parseIdToken(idToken) : undefined;
        const username = claims ? (claims['cognito:username'] || claims.sub) : undefined;
        if (!username) {
          return null;
        }
        authParameters.SECRET_HASH = await this.generateSecretHash(username);
      }

      const response = await fetch(`https://cognito-idp.${this.config.region}.amazonaws.com/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth',
        },
        body: JSON.stringify({
          AuthFlow: 'REFRESH_TOKEN_AUTH',
          ClientId: this.config.clientId,
          AuthParameters: authParameters,
        }),
      });

      if (!response.ok) {
        // Expired/revoked refresh token (NotAuthorizedException) lands here.
        return null;
      }

      const data = await response.json();
      const result = data?.AuthenticationResult;
      if (!result?.AccessToken || !result?.IdToken) {
        return null;
      }

      // REFRESH_TOKEN_AUTH returns no new refresh token — reuse the current one.
      this.storeTokens(result.AccessToken, result.IdToken, refreshToken);
      return { accessToken: result.AccessToken, idToken: result.IdToken };
    } catch (error) {
      // Network/parse failure — surface as a failed refresh. Log no token bytes.
      console.error('Cognito token refresh failed');
      Sentry.captureException(error);
      return null;
    }
  }

  /** Public token clear for the refresh helper to call when a refresh fails. */
  clearStoredTokens(): void {
    this.clearTokens();
  }

  async signOut(): Promise<void> {
    try {
      const accessToken = localStorage.getItem('cognito_access_token');
      if (accessToken) {
        await fetch(`https://cognito-idp.${this.config.region}.amazonaws.com/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-amz-json-1.1',
            'X-Amz-Target': 'AWSCognitoIdentityProviderService.GlobalSignOut',
          },
          body: JSON.stringify({
            AccessToken: accessToken,
          }),
        });
      }
    } catch (error) {
      console.error('Cognito sign out error:', error);
      Sentry.captureException(error);
    } finally {
      this.clearTokens();
    }
  }

  async getCurrentUser(): Promise<CognitoUser | null> {
    try {
      const accessToken = localStorage.getItem('cognito_access_token');
      const idToken = localStorage.getItem('cognito_id_token');
      
      if (!accessToken || !idToken) {
        return null;
      }

      // Parse user info from stored ID token
      const userInfo = this.parseIdToken(idToken);
      
      return {
        id: userInfo.sub,
        username: userInfo.email,
        email: userInfo.email,
        isAuthenticated: true,
        accessToken,
        idToken,
        refreshToken: localStorage.getItem('cognito_refresh_token') || undefined,
      };
    } catch (error) {
      console.error('Error getting current user:', error);
      Sentry.captureException(error);
      this.clearTokens();
      return null;
    }
  }

  private parseIdToken(idToken: string): any {
    try {
      // Basic JWT parsing (without verification for client-side)
      const payload = idToken.split('.')[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);
    } catch (error) {
      console.error('Error parsing ID token:', error);
      Sentry.captureException(error);
      throw new Error('Invalid token');
    }
  }

  private storeTokens(accessToken: string, idToken: string, refreshToken: string): void {
    localStorage.setItem('cognito_access_token', accessToken);
    localStorage.setItem('cognito_id_token', idToken);
    localStorage.setItem('cognito_refresh_token', refreshToken);
  }

  private clearTokens(): void {
    localStorage.removeItem('cognito_access_token');
    localStorage.removeItem('cognito_id_token');
    localStorage.removeItem('cognito_refresh_token');
  }

  private async generateSecretHash(username: string): Promise<string> {
    if (!this.config.clientSecret) {
      throw new Error('Client secret is required for SECRET_HASH generation');
    }

    // SECRET_HASH is calculated as HMAC-SHA256 of (username + clientId) using clientSecret as key
    const message = username + this.config.clientId;
    
    // Use Web Crypto API for HMAC-SHA256
    const encoder = new TextEncoder();
    const keyData = encoder.encode(this.config.clientSecret);
    const messageData = encoder.encode(message);
    
    // Import the key for HMAC
    const cryptoKey = await window.crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );
    
    // Sign the message
    const signature = await window.crypto.subtle.sign('HMAC', cryptoKey, messageData);
    
    // Convert to base64
    return btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(signature))));
  }

}

export default CognitoAuthService;